import React from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import CalendarDayTimeGrid from './CalendarDayTimeGrid';
import CalendarTimeBlock from './CalendarTimeBlock';
import DayTabs from './CalendarDayTabs';
import { getDailyAppointmentStyle } from './calendarUtils';
import useTimezone from '../../hooks/useTimezone';

const CELL_WIDTH = 300;
const HOUR_HEIGHT = 60;
const START_HOUR = 8;
const END_HOUR = 20;
const TIME_WIDTH = 85;

const CalendarDayView = ({
  date,
  sessions = [],
  eventBlocks = [],
  availabilityBlocks = [],
  boxStyles,
  useSummaries = false,
  hourWidth = TIME_WIDTH,
  cellWidth = CELL_WIDTH,
  hourHeight = HOUR_HEIGHT,
  startHour = START_HOUR,
  endHour = END_HOUR,
  onDateChange = (e, date) => console.log('Change date', date),
  onEditSession = (e, id) => console.log('Edit session', id),
  onClickAvailability = (e, id) => console.log('Click availability', id),
}) => {
  const timezone = useTimezone();
  const baseDate = date
    ? (typeof date === 'string' ? new Date(date) : date)
    : new Date();
  const totalWidth = hourWidth + cellWidth;
  const dateIsToday = baseDate.toDateString() === new Date().toDateString();
  // display time in user's timezone
  return (
    <Box 
      sx={{
        ...boxStyles, 
        p: {xs: 0, sm: 1, md: 2},
        border: {
          md: boxStyles?.border,
          xs: null,
        },
      }}
    >
      <Box 
        style={{minHeight: hourHeight, width: '100%'}}
      >
        <Stack direction='row'>
          <IconButton
            onClick={e => onDateChange(e, new Date(baseDate.getTime() - 7 * 24 * 60 * 60 * 1000))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={e => onDateChange(e, new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000))}
          >
            <ChevronRightIcon />
          </IconButton>
          {!dateIsToday &&
            <Button variant='text' onClick={e => onDateChange(e, new Date())}>
              Today
            </Button>
          }
        </Stack>
        <Stack 
          direction='row' 
          spacing={2} 
          alignItems='center' 
          justifyContent='center'
          sx={{p: {xs: 0, smmd: 2}}}
          style={{width: '100%'}}
        >
          <DayTabs date={date} onChange={(e, dt) => onDateChange(e, dt)} />
        </Stack>
      </Box>
      <Stack direction='row' justifyContent='center'>
        <Box sx={{ 
          overflowX: 'auto',
          minWidth: totalWidth,
          width: totalWidth,
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <CalendarDayTimeGrid
              timezone={timezone} 
              cellWidth={cellWidth}
              hourWidth={hourWidth}
              hourHeight={hourHeight}
              numHours={endHour - startHour}
              startHour={startHour}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: totalWidth,
                height: '100%',
                // pointerEvents: 'none',
              }}
            >
            {sessions
              .map(s => ({
                session: s, 
                style: s ? getDailyAppointmentStyle({
                    session: s,
                    timezone,
                    hourWidth,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                }) : null
              }))
              .filter(({ session, style }) => !!session || !!style)
              .map(({ session, style }, index) => (
                <CalendarTimeBlock
                  timezone={timezone}
                  key={index}
                  block={session}
                  style={style}
                  onClick={e => onEditSession(e, session.id)}
                />
              ))
            }
            {
              eventBlocks
                .filter(block => moment(block.date).isSame(moment(baseDate), 'day'))
                .map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`event-${index}`}
                  block={block}
                  style={getDailyAppointmentStyle({
                    session: block,
                    timezone,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                />
              )
            }
            {
              availabilityBlocks
                .filter(block => moment(block.date).isSame(moment(baseDate), 'day'))
                .map((block, index) =>
                <CalendarTimeBlock
                  timezone={timezone}
                  key={`avail-${index}`}
                  block={block}
                  style={getDailyAppointmentStyle({
                    session: block,
                    timezone,
                    hourWidth,
                    cellWidth,
                    hourHeight,
                    startHour,
                    endHour,
                  })}
                  isAvailabilityBlock={true}
                  useSummaries={useSummaries}
                  onClick={(e, id) => onClickAvailability && onClickAvailability(e, id)}
                />
              )
            }
          </Box>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default CalendarDayView;